import { gsap } from 'gsap';
import type { ThreeStage } from '../../../../three/ThreeStage';
import { BaseScene } from '../../../../core/BaseScene';
import { brandLogoSvg } from '../../../../components/brandLogo';
import { seeded } from '../../../../components/random';
import { draw, pulse } from '../../../../components/svg';
import { words, revealWords, settleWords } from '../../../../components/text';

/** The separate places information lives (Prompt2 §3). */
const SOURCES = ['Documents', 'Data', 'Research', 'Systems', 'Reports', 'Knowledge', 'Operations', 'External information'];

/** What information actually looks like when it arrives: specific, unsorted, endless. */
const FRAGMENTS = [
  'Q3 board report.pdf', 'market research 2024', 'supplier_metrics.xlsx', 'incident log', 'customer survey',
  'policy v4.docx', 'sensor feed · 14:02', 'API · orders', 'benchmark study', 'KPI dashboard', 'meeting transcript',
  'sector news', 'ops data · batch 118', 'knowledge base', 'research paper', 'CRM export', 'annual review',
  'usage metrics', 'contract archive', 'regulatory update', 'analyst note', 'forecast model', 'support tickets',
];

/** MINT's core transformation. */
const FLOW = ['Information', 'Context', 'Connection', 'Reasoning', 'Intelligence'];
const PIPE = { x1: 440, x2: 1160, y: 470 };
const STAGE_X = FLOW.map((_, i) => PIPE.x1 + 70 + i * ((PIPE.x2 - PIPE.x1 - 140) / (FLOW.length - 1)));
const SOURCE_Y = SOURCES.map((_, i) => 190 + i * 80);

/**
 * 0 Information lives everywhere — INFORMATION EVERYWHERE.
 * 1 The flood slows; nothing adds up — UNDERSTANDING NOWHERE.
 * 2 The information converges, and MINT appears in its path
 * 3 Information → context → connection → reasoning → intelligence
 * 4 ONE INTELLIGENCE FOUNDATION.
 */
export class MintCoreScene extends BaseScene {
  public readonly stepCount = 5;

  private lanes: gsap.core.Tween[] = [];
  private flowing = { inflow: false, outflow: false };

  protected template(): string {
    const random = seeded(305);
    const lanes = Array.from({ length: 11 }, (_, lane) => {
      const items = Array.from({ length: 7 }, () => FRAGMENTS[Math.floor(random() * FRAGMENTS.length)]);
      const run = items.map((item) => `<span>${item}</span>`).join('');
      const depth = random();
      return `<div class="stream" style="--y:${(8 + lane * 8.4).toFixed(1)};--depth:${depth.toFixed(2)}">
        <b class="stream__tag">${SOURCES[lane % SOURCES.length]}</b>
        <div class="stream__track">${run}${run}</div>
      </div>`;
    }).join('');

    const inflow = SOURCES.map((label, i) => {
      const y = SOURCE_Y[i];
      return `<g class="mint-source" data-from="2">
          <path class="mint-in draw" pathLength="1" d="M160 ${y} C300 ${y} 330 ${PIPE.y} ${PIPE.x1} ${PIPE.y}"/>
          <circle class="mint-source__dot" cx="160" cy="${y}" r="4"/>
          <text class="mint-source__label" x="140" y="${y + 5}" text-anchor="end">${label}</text>
        </g>`;
    }).join('');

    const stages = FLOW.map((stage, i) => `
      <g class="mint-stage" data-stage="${i}">
        <circle class="mint-stage__ring" cx="${STAGE_X[i]}" cy="${PIPE.y - 14}" r="13"/>
        <circle class="mint-stage__dot" cx="${STAGE_X[i]}" cy="${PIPE.y - 14}" r="5"/>
        <text class="mint-stage__label" x="${STAGE_X[i]}" y="${PIPE.y + 34}" text-anchor="middle">${stage.toUpperCase()}</text>
      </g>`).join('');

    // What leaves MINT is intelligence someone can use.
    const outflow = `
      <g class="mint-output" data-from="3">
        <path class="mint-out draw" pathLength="1" d="M${PIPE.x2} ${PIPE.y - 14} H1330"/>
        <circle class="mint-output__dot" cx="1340" cy="${PIPE.y - 14}" r="9"/>
        <text class="mint-output__label" x="1340" y="${PIPE.y + 30}" text-anchor="middle">USABLE</text>
      </g>`;

    const inPulses = SOURCES.map(() => '<circle class="mint-pulse mint-pulse--in" r="3.5"/>').join('');
    const pipePulses = [0, 1, 2].map(() => '<circle class="mint-pulse mint-pulse--pipe" r="4.5"/>').join('');
    const outPulses = '<circle class="mint-pulse mint-pulse--out" r="5"/>';

    return `
      <div class="mint-streams" aria-hidden="true">${lanes}</div>
      <div class="scene-layout mint-problem">
        <div class="statement-stack mint-flood-copy">
          <h2 class="statement" data-from="0" data-until="1">${words('INFORMATION EVERYWHERE.')}</h2>
          <h2 class="statement statement--accent" data-from="1" data-until="1">${words('UNDERSTANDING NOWHERE.')}</h2>
        </div>
        <svg class="diagram mint-diagram" viewBox="0 0 1600 900" role="img" aria-label="Information converging into MINT: ${FLOW.join(', ')}">
          ${inflow}
          <g class="mint-core" data-from="2">
            <rect class="mint-core__body" x="${PIPE.x1}" y="${PIPE.y - 72}" width="${PIPE.x2 - PIPE.x1}" height="144" rx="72"/>
            <path class="mint-pipe" d="M${PIPE.x1} ${PIPE.y - 14} H${PIPE.x2}"/>
            ${brandLogoSvg('mint', { x: (PIPE.x1 + PIPE.x2) / 2, y: PIPE.y - 128, height: 58, surface: 'stage', className: 'mint-core__name' })}
            ${stages}
          </g>
          ${outflow}
          <g class="mint-pulses">${inPulses}${pipePulses}${outPulses}</g>
        </svg>
        <h2 class="statement statement--accent mint-foundation" data-from="4">${words('ONE INTELLIGENCE FOUNDATION.')}</h2>
      </div>`;
  }

  protected onEnter(immediate: boolean): void {
    this.ensureLanes();
    if (immediate || this.step !== 0) return;
    const tl = this.timeline();
    tl.fromTo(this.$$('.stream'), { opacity: 0 }, { opacity: 1, duration: 1.2, stagger: 0.08, ease: 'power2.out' }, 0);
    tl.add(revealWords(this.$('.mint-flood-copy .statement')), 0.6);
  }

  protected onExit(): void {
    this.lanes = [];
    this.flowing = { inflow: false, outflow: false };
  }

  protected onStep(step: number, stage: ThreeStage, immediate: boolean, previousStep: number): void {
    stage.setAccent(this.meta.accent);
    stage.setMode('mint-core', step, immediate);
    this.ensureLanes();

    const forward = !immediate && step > previousStep;
    const d = immediate ? 0 : 1;
    const tl = this.timeline();

    // The entrance fade may have been cut short by a fast advance; lanes always settle visible.
    gsap.set(this.$$('.stream'), { opacity: 1 });

    // The flood: everywhere at first, then it slows and dims (the pause) before converging.
    const flooding = step <= 1;
    this.lanes.forEach((lane) => gsap.to(lane, { timeScale: step === 1 ? 0.25 : 1, duration: d * 1.6, ease: 'power2.inOut' }));
    gsap.to(this.$('.mint-streams'), { opacity: step === 0 ? 1 : step === 1 ? 0.45 : 0, duration: d * (flooding ? 1.2 : 0.9), ease: 'power2.inOut', overwrite: 'auto' });

    this.$$('.statement').forEach((statement) => settleWords(statement));
    if (forward && step === 1) tl.add(revealWords(this.$$('.mint-flood-copy .statement')[1]), 0.1);

    // Paths drawn for everything visible at this step; newly introduced paths animate.
    this.$$<SVGPathElement>('.mint-in').forEach((path) => {
      if (!(forward && step === 2)) draw(path, step >= 2, { immediate: true });
    });
    this.$$<SVGPathElement>('.mint-out').forEach((path) => {
      if (!(forward && step === 3)) draw(path, step >= 3, { immediate: true });
    });

    // 2 — MINT appears in the path of the information and is named.
    const name = this.$('.mint-core__name');
    if (forward && step === 2) {
      tl.fromTo(this.$('.mint-core'), { scale: 0.9, transformOrigin: '50% 50%' }, { scale: 1, duration: 1.2, ease: 'expo.out' }, 0);
      tl.add(draw(this.$$('.mint-in'), true, { duration: 1.1, stagger: 0.06, ease: 'power2.out' }), 0.2);
      tl.fromTo(name, { opacity: 0, y: 18 }, { opacity: 1, y: 0, duration: 1.3, ease: 'power3.out' }, 0.9);
    } else {
      gsap.set(name, { opacity: 1, y: 0 });
    }

    // 3 — the core transformation, one stage at a time.
    const stageDots = this.$$('.mint-stage');
    if (forward && step === 3) {
      stageDots.forEach((el) => el.classList.remove('is-lit'));
      stageDots.forEach((el, i) => tl.call(() => el.classList.add('is-lit'), [], 0.2 + i * 0.4));
      tl.add(draw(this.$$('.mint-out'), true, { duration: 0.9, ease: 'power2.out' }), 2.2);
      tl.fromTo(this.$$('.mint-output__label'), { opacity: 0 }, { opacity: 1, duration: 0.6 }, 2.8);
    } else {
      stageDots.forEach((el) => el.classList.toggle('is-lit', step >= 3));
      gsap.set(this.$$('.mint-output__label'), { opacity: 1 });
    }

    // 4 — the diagram steps back for the statement.
    gsap.to(this.$('.mint-diagram'), { opacity: step === 4 ? 0.16 : 1, scale: step === 4 ? 0.96 : 1, transformOrigin: '50% 50%', duration: d * 1, ease: 'power2.inOut' });
    if (forward && step === 4) tl.add(revealWords(this.$('.mint-foundation')), 0.4);

    this.syncPulses(step, forward);
  }

  /** Inflow is irregular (noise); the pipeline and outputs are steady (order). */
  private syncPulses(step: number, forward: boolean): void {
    const wantIn = step >= 2;
    const wantOut = step >= 3;
    if (wantIn && !this.flowing.inflow) {
      const inPaths = this.$$<SVGPathElement>('.mint-in');
      this.$$<SVGCircleElement>('.mint-pulse--in').forEach((marker, i) => {
        this.loop(pulse(marker, inPaths[i], { duration: 1.2 + (i % 3) * 0.45, delay: (forward ? 1.2 : 0) + i * 0.23, repeatDelay: 0.4 + (i % 4) * 0.35 }));
      });
      const pipe = this.$<SVGPathElement>('.mint-pipe');
      this.$$<SVGCircleElement>('.mint-pulse--pipe').forEach((marker, i) => {
        this.loop(pulse(marker, pipe, { duration: 2.2, delay: (forward ? 1.6 : 0) + i * 0.75, repeatDelay: 0, ease: 'none' }));
      });
      this.flowing.inflow = true;
    }
    if (wantOut && !this.flowing.outflow) {
      const outPaths = this.$$<SVGPathElement>('.mint-out');
      this.$$<SVGCircleElement>('.mint-pulse--out').forEach((marker, i) => {
        this.loop(pulse(marker, outPaths[i], { duration: 1.1, delay: (forward ? 0.9 : 0) + i * 0.4, repeatDelay: 1.1 }));
      });
      this.flowing.outflow = true;
    }
    if (!wantIn && this.flowing.inflow) {
      this.restartAmbient();
    } else if (!wantOut && this.flowing.outflow) {
      this.restartAmbient();
      this.syncPulses(step, false);
    }
  }

  /** Kill every loop (pulses and lanes), then bring the lanes back. Used when stepping backwards. */
  private restartAmbient(): void {
    this.stopLoops();
    this.lanes = [];
    this.flowing = { inflow: false, outflow: false };
    this.$$('.mint-pulse').forEach((marker) => { marker.style.opacity = '0'; });
    this.ensureLanes();
  }

  private ensureLanes(): void {
    if (this.lanes.length) return;
    this.lanes = this.$$('.stream__track').map((track, i) =>
      this.loop(gsap.fromTo(track, { xPercent: -50 }, { xPercent: 0, duration: 38 + (i % 5) * 7, ease: 'none', repeat: -1 })));
  }
}
